import { JobNode } from "../job-node";

export const nodeTypes = { jobNode: JobNode };

const nodeStyle = {
    width: 300,
    padding: 0,
    border: "1px solid #ddd",
    borderRadius: 8,
    background: "#fff",
    overflow: "visible",
};

/**
 * @param {Object[]} jobs
 * @param {Object[]} moduleVersions
 * @param {Set<string>} [editableJobNames] - job names whose argument
 *   fields should be editable; every other node renders read-only. When
 *   omitted, all nodes are read-only.
 * @param {string} [missionUuid] - the current pipeline instance's mission
 *   UUID, forwarded to job nodes that need to look up sibling missions
 *   (e.g. `anotherDateSelection`).
 * @param {string[]} [experimentUuids] - the current pipeline instance's
 *   experiment UUIDs, forwarded to job nodes that need to filter sibling
 *   missions' pipeline instances (e.g. `anotherDateSelection`).
 * @param {string} [experimentCropUuid] - crop of the associated experiment,
 *   used by the trained-model selector to surface its crop's models first.
 */
export function getNodes(
    jobs,
    moduleVersions,
    editableJobNames,
    missionUuid,
    experimentUuids,
    experimentCropUuid
) {
    const positions = calculateNodePositions(jobs);

    const nodes = jobs.map((job) => {
        // Get the module version of the job
        const moduleVersion = moduleVersions.find(
            (mv) => mv.uuid === job.mainModuleVersionUuid
        );
        if (!moduleVersion)
            throw new Error(
                `Module version not found: ${job.mainModuleVersionUuid}`
            );

        const disabled = !editableJobNames?.has(job.name);

        return {
            type: "jobNode",
            id: job.name,
            position: positions.get(job.name),
            data: {
                job,
                moduleVersion,
                disabled,
                missionUuid,
                experimentUuids,
                experimentCropUuid,
            },
            style: nodeStyle,
        };
    });

    return nodes;
}

const NODE_HEIGHT = 100;
const NODE_HORIZONTAL_SPACING = 350;
const NODE_VERTICAL_SPACING = 100;

/**
 * Calculate React Flow node positions based on the dependencies between the
 * jobs.
 *
 * Each job is placed at a "level" equal to the longest dependency chain
 * leading to it (jobs with no dependencies sit at level 0). Levels are laid
 * out left-to-right along the x-axis; jobs sharing a level are stacked
 * vertically and centered around a common baseline. Ties within a level are
 * broken by the order in which jobs are first visited by the topological
 * traversal (which starts from the input array order).
 *
 * @param {{
 *   name: string,
 *   dependencies: string[],
 * }[]} jobs
 * @returns {Map<string, { x: number, y: number }>} Positions keyed by job
 *   name.
 */
function calculateNodePositions(jobs) {
    const jobsByName = new Map(jobs.map((job) => [job.name, job]));

    // Compute each job's level via DFS, recording the visit order per level
    // so that ties within a level follow the same ordering as the old
    // implementation.
    const levels = new Map();
    const namesByLevel = new Map();

    const computeLevel = (name) => {
        if (levels.has(name)) return levels.get(name);

        const job = jobsByName.get(name);
        const deps = job?.dependencies ?? [];
        const level =
            deps.length === 0 ? 0 : Math.max(...deps.map(computeLevel)) + 1;

        levels.set(name, level);
        if (!namesByLevel.has(level)) namesByLevel.set(level, []);
        namesByLevel.get(level).push(name);
        return level;
    };
    jobs.forEach((job) => computeLevel(job.name));

    // Assign positions: x from the level, y centered around the level's
    // midpoint.
    const positions = new Map();
    namesByLevel.forEach((names, level) => {
        const total = names.length;
        names.forEach((name, indexInLevel) => {
            positions.set(name, {
                x: level * NODE_HORIZONTAL_SPACING + 50,
                y:
                    (indexInLevel - (total - 1) / 2) *
                        (NODE_HEIGHT + NODE_VERTICAL_SPACING) +
                    200,
            });
        });
    });

    return positions;
}

export function getEdges(jobs) {
    const edges = [];

    jobs.forEach((job) => {
        if (job.dependencies && job.dependencies.length > 0) {
            job.dependencies.forEach((dep) => {
                edges.push({
                    id: `${dep}-${job.name}`,
                    source: dep,
                    target: job.name,
                    type: "smoothstep",
                    animated: true,
                    style: { stroke: "#888", strokeWidth: 1 },
                });
            });
        }
    });

    return edges;
}
